/**
 * @file WhosThisFor.jsx
 * @description AI EXPO JORDAN 2026 — "Who's This For?" section.
 *   Redesigned to match the Command Bridge visual language used across the
 *   site (see About). Brand discipline:
 *     · Base palette: dark gray #151515 / black, white text
 *     · Accents (purple #7a3cff, azure #2cabe2) reserved for hover and
 *       animation only — never resting fills
 *     · Oswald display + JetBrains Mono for HUD/technical labels
 *
 *   Two audience panels sit side-by-side as terminal-style "channels."
 *   Participants channel uses azure on hover; Visitors channel uses
 *   purple on hover. Content is preserved verbatim.
 *
 * @author AI EXPO Web Team
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../ComponentsStyling/WhosThisFor.css';

/* -------------------------------------------------------------------------- */
/*  Section content                                                           */
/* -------------------------------------------------------------------------- */

const ENTRIES = [
    {
        index: '01',
        kicker: 'For the makers',
        role: 'Participants',
        headline: 'You came to build.',
        accent: 'azure',
        body: (
            <>
                If you&apos;re a <b>student, a graduation team, or a startup</b> with an AI
                project ready to demonstrate — this event was made for you. You apply,
                go through review, and present your work live to a professional judging
                panel and the wider tech community.
            </>
        ),
        items: [
            'Apply to compete with your AI project before 20 July.',
            'Set up a booth and run live demos throughout the day.',
            'Pitch to judges across three tracks and five sectors.',
        ],
        meta: 'SUBMISSION WINDOW · JULY 10 – 20, 2026',
        cta: { label: 'Register your project', to: '/program#track-selector' },
    },
    {
        index: '02',
        kicker: 'For the curious',
        role: 'Visitors',
        headline: 'You came to witness.',
        accent: 'purple',
        body: (
            <>
                If you&apos;re <b>curious about AI</b> — a student, a professional, an
                enthusiast — this is a day to walk through real systems running live,
                meet the people who built them, and find your next conversation.
                No project required. Just a ticket.
            </>
        ),
        items: [
            'Buy a Standard or Gold ticket and walk the floor.',
            'See real AI projects running live, not on slides.',
            'Attend stage talks, ask questions, network with builders.',
        ],
        meta: 'STANDARD · COMING SOON     //     GOLD · COMING SOON',
        cta: { label: 'Explore Expo Day', to: '/expo-day' },
    },
];

/* -------------------------------------------------------------------------- */
/*  Mouse-follow spotlight                                                    */
/* -------------------------------------------------------------------------- */

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useSpotlight = (ref) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;

        let raf = 0;
        let pending = null;

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            pending = {
                x: ((e.clientX - rect.left) / rect.width) * 100,
                y: ((e.clientY - rect.top) / rect.height) * 100,
            };
            if (!raf) {
                raf = requestAnimationFrame(() => {
                    if (pending) {
                        el.style.setProperty('--mx', pending.x + '%');
                        el.style.setProperty('--my', pending.y + '%');
                    }
                    raf = 0;
                });
            }
        };

        el.addEventListener('mousemove', onMove);
        return () => {
            el.removeEventListener('mousemove', onMove);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref]);
};

/* -------------------------------------------------------------------------- */
/*  Section                                                                   */
/* -------------------------------------------------------------------------- */

const WhosThisFor = () => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useSpotlight(ref);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    setVisible(true);
                    obs.unobserve(el);
                }
            });
        }, { threshold: 0.12 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className={`wtf${visible ? ' is-visible' : ''}`}
            id="whos-this-for"
        >
            {/* Background layers */}
            <div className="wtf__grid" aria-hidden />
            <div className="wtf__spot" aria-hidden />
            <div className="wtf__noise" aria-hidden />

            <div className="wtf__wrap">

                {/* ───────── HUD STRIP ───────── */}
                <div className="wtf__hud" aria-hidden>
                    <span className="wtf__hud-tag">
                        <span className="wtf__hud-dot" />
                        SEC&nbsp;//&nbsp;03
                    </span>
                    <span className="wtf__hud-line" />
                    <span className="wtf__hud-mid">AUDIENCE&nbsp;·&nbsp;TWO&nbsp;CHANNELS</span>
                    <span className="wtf__hud-line" />
                    <span className="wtf__hud-tag wtf__hud-tag--right">
                        EDITION&nbsp;01
                        <span className="wtf__hud-blink" />
                    </span>
                </div>

                {/* ───────── HEADER ───────── */}
                <header className="wtf__head">
                    <div className="wtf__eyebrow">
                        <span className="wtf__eyebrow-bracket">[</span>
                        <span>Who is this for?</span>
                        <span className="wtf__eyebrow-bracket">]</span>
                    </div>

                    <h2 className="wtf__title">
                        <span className="wtf__title-row">
                            <span className="wtf__title-num" aria-hidden>02</span>
                            <span className="wtf__title-text">Two audiences.</span>
                        </span>
                        <span className="wtf__title-row wtf__title-row--alt">
                            <span className="wtf__title-text">Two</span>
                            <span className="wtf__title-text wtf__title-text--outline">days.</span>
                        </span>
                    </h2>

                    <div className="wtf__lede-wrap">
                        <span className="wtf__lede-bar" aria-hidden />
                        <p className="wtf__lede">
                            AI Expo Jordan 2026 was designed around two kinds of people —
                            the ones showing what they built, and the ones who came to see
                            it. Find the one that fits.
                        </p>
                    </div>
                </header>

                {/* ───────── ENTRIES ───────── */}
                <div className="wtf__entries">
                    {ENTRIES.map((e, i) => (
                        <article
                            key={e.index}
                            className={`wtf-entry wtf-entry--${e.accent}`}
                            style={{ '--i': i }}
                        >
                            {/* Corner brackets */}
                            <span className="wtf-entry__corner wtf-entry__corner--tl" />
                            <span className="wtf-entry__corner wtf-entry__corner--tr" />
                            <span className="wtf-entry__corner wtf-entry__corner--bl" />
                            <span className="wtf-entry__corner wtf-entry__corner--br" />

                            {/* Sweep gradient on hover */}
                            <span className="wtf-entry__sweep" aria-hidden />

                            {/* Channel header strip */}
                            <div className="wtf-entry__strip">
                                <span className="wtf-entry__strip-id">
                                    CH/{e.index}
                                </span>
                                <span className="wtf-entry__strip-status">
                                    <span className="wtf-entry__strip-led" />
                                    {e.kicker.toUpperCase()}
                                </span>
                            </div>

                            {/* Monumental numeral background */}
                            <div className="wtf-entry__numeral" aria-hidden>
                                {e.index}
                            </div>

                            <div className="wtf-entry__inner">

                                <div className="wtf-entry__role">
                                    <span className="wtf-entry__role-label">{e.role}</span>
                                    <span className="wtf-entry__role-rule" />
                                </div>

                                <h3 className="wtf-entry__headline">
                                    {e.headline}
                                </h3>

                                <p className="wtf-entry__body">{e.body}</p>

                                <ol className="wtf-entry__list">
                                    {e.items.map((item, j) => (
                                        <li key={j} style={{ '--j': j }}>
                                            <span className="wtf-entry__list-num">
                                                {String(j + 1).padStart(2, '0')}
                                            </span>
                                            <span className="wtf-entry__list-bar" />
                                            <span className="wtf-entry__list-text">
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ol>

                                <div className="wtf-entry__foot">
                                    <span className="wtf-entry__meta">
                                        <span className="wtf-entry__meta-tick" />
                                        {e.meta}
                                    </span>
                                    <Link to={e.cta.to} className="wtf-entry__cta">
                                        <span className="wtf-entry__cta-bracket">[</span>
                                        <span className="wtf-entry__cta-text">
                                            {e.cta.label}
                                        </span>
                                        <span className="wtf-entry__cta-arrow">
                                            <svg viewBox="0 0 24 12" fill="none" aria-hidden>
                                                <path
                                                    d="M1 6h22m0 0L18 1m5 5l-5 5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.4"
                                                    strokeLinecap="square"
                                                />
                                            </svg>
                                        </span>
                                        <span className="wtf-entry__cta-bracket">]</span>
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* ───────── CLOSE ───────── */}
                <footer className="wtf__close">
                    <div className="wtf__close-rule">
                        <span className="wtf__close-rule-mark">CONVERGENCE</span>
                        <span className="wtf__close-rule-line" />
                    </div>
                    <p className="wtf__close-text">
                        Whichever path you take, you end up in the
                        same <em>conversation</em> — on <b>Expo Day, 1 &amp; 3 October 2026</b>,
                        at <b>UJ Academy</b>.
                    </p>
                </footer>

            </div>
        </section>
    );
};

export default WhosThisFor;